import { useState, useEffect } from 'react';

import { LIGHT_BACKGROUND_PAGES } from '@/components/layout/constants';
import { createOptimizedScrollHandler } from '@/components/utils/layoutOptimization';

export const useHeaderOnLight = (currentPageName: string) => {
  // Initialize headerOnLight based on current page and scroll position to prevent invisible text on first render
  const [headerOnLight, setHeaderOnLight] = useState(() => {
    if (typeof window === 'undefined') {
      return true; // Default to visible for SSR
    }
    if (LIGHT_BACKGROUND_PAGES.includes(currentPageName)) {
      return true;
    } else if (currentPageName === 'Home') {
      return true; // Always visible on Home page
    } else {
      // Default for other pages: transparent unless scrolled past a threshold
      return window.scrollY > 100;
    }
  });

  // Optimized scroll handler to prevent forced reflows for headerOnLight state
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleOptimizedScroll = createOptimizedScrollHandler(scrollY => {
      try {
        if (currentPageName === 'Home') {
          setHeaderOnLight(true); // Always visible on Home page
        } else {
          // Use the predefined list
          if (LIGHT_BACKGROUND_PAGES.includes(currentPageName)) {
            setHeaderOnLight(true);
          } else {
            setHeaderOnLight(scrollY > 100);
          }
        }
      } catch {
        // Error in optimized scroll handler
      }
    });

    window.addEventListener('scroll', handleOptimizedScroll, { passive: true });
    // Call immediately to set initial state
    handleOptimizedScroll();

    return () => {
      window.removeEventListener('scroll', handleOptimizedScroll);
    };
  }, [currentPageName]);

  // Update headerOnLight immediately when currentPageName changes
  useEffect(() => {
    if (typeof window === 'undefined') return; // Don't run on SSR

    if (LIGHT_BACKGROUND_PAGES.includes(currentPageName)) {
      setHeaderOnLight(true);
    } else if (currentPageName === 'Home') {
      setHeaderOnLight(true); // Always visible on Home page
    } else {
      setHeaderOnLight(window.scrollY > 100);
    }
  }, [currentPageName]);

  return headerOnLight;
};
